import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Table,
} from 'reactstrap';

import apiOffers from 'api/offers';
import IOfferTextTemplate from 'api/interfaces/offers/IOfferTextTemplate';
import IUserGet from 'api/interfaces/users/IUserGet';
import LinkButton from 'components/layout/buttons/LinkButton';
import ModalConfirm from 'components/layout/modals/ModalConfirm';
import { DELETE_OFFER_TEXTS } from 'services/enums/users/permissions';
import { formatServerDate } from 'services/dates/serverTime';
import translations from 'services/translations/translations';
import {
  appendOfferText,
  offerTextToPlainText,
} from 'services/offers/offerText';
import { addMessage } from 'store/messages/actions';

import RichOfferTextEditor from '../offerProduct/calculationWindow/RichOfferTextEditor';

import 'style/layout/modal/ModalConfirm.css';
import 'style/offers/OfferTextField.css';

const PREVIEW_LENGTH = 160;

// In the time of the browser: the server sends UTC
const formatDate = (dateTime: string): string => formatServerDate(dateTime);

/**
 * The general offer text of an offer with text formatting, saving the text to the offer
 * text archive and fetching a text from the archive.
 */
const OfferTextField = ({
  label,
  locked,
  onChange,
  tall,
  textType,
  value,
}: {
  label: string;
  locked: boolean;
  onChange: (value: string) => void;
  // About the size of a PDF page, for a text printed on a page of its own
  tall?: boolean;
  // The kind of the text and its archive, e.g. delivery-terms; the offer text without it
  textType?: string;
  value: string;
}): JSX.Element => {
  const dispatch = useDispatch();
  const { t } = useTranslation([translations.offers, translations.common]);
  // The archive texts of the kind, e.g. "Save delivery terms"
  const textsKey =
    textType === 'delivery-terms' ? 'deliveryTermsTexts' : 'offerTexts';

  const [saveIsOpen, setSaveIsOpen] = useState(false);
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);

  const [searchIsOpen, setSearchIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [templates, setTemplates] = useState<IOfferTextTemplate[] | null>(null);

  const [templateToDelete, setTemplateToDelete] =
    useState<IOfferTextTemplate>();

  const currentUser = useSelector(
    (state: { users: { current: IUserGet | null } }) => state.users.current
  );

  const mayDeleteAllTexts =
    currentUser?.permissions?.some(
      (permission) => permission.type === DELETE_OFFER_TEXTS
    ) ?? false;

  // Own texts can always be deleted, other texts with the permission (e.g. admins)
  const canDelete = (template: IOfferTextTemplate) =>
    mayDeleteAllTexts ||
    (currentUser?.id !== undefined &&
      template.createdUserId === currentUser.id);

  const textIsEmpty = offerTextToPlainText(value) === '';

  const deleteTemplate = async () => {
    const template = templateToDelete;
    setTemplateToDelete(undefined);

    if (template === undefined) {
      return;
    }

    try {
      await apiOffers.textTemplates.delete(template.id);
      setTemplates(
        (templates ?? []).filter((existing) => existing.id !== template.id)
      );
      dispatch(addMessage(t(`${translations.offers}:${textsKey}.Deleted`)));
    } catch (error) {
      dispatch(
        addMessage(t(`${translations.offers}:${textsKey}.Delete failed`))
      );
    }
  };

  // Search as the user types, like the other searches
  useEffect(() => {
    if (!searchIsOpen) {
      return undefined;
    }

    let cancelled = false;
    const timeout = setTimeout(async () => {
      try {
        const found = await apiOffers.textTemplates.get(
          search.trim(),
          textType
        );
        if (!cancelled) {
          setTemplates(found);
        }
      } catch (error) {
        if (!cancelled) {
          setTemplates([]);
        }
      }
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
  }, [searchIsOpen, search, textType]);

  const openSave = () => {
    setName('');
    setSaveIsOpen(true);
  };

  const saveTemplate = async () => {
    if (name.trim() === '') {
      return;
    }

    setSaving(true);

    try {
      await apiOffers.textTemplates.post(name.trim(), value, textType);
      setSaveIsOpen(false);
      dispatch(addMessage(t(`${translations.offers}:${textsKey}.Saved`)));
    } catch (error) {
      dispatch(addMessage(t(`${translations.offers}:${textsKey}.Save failed`)));
    } finally {
      setSaving(false);
    }
  };

  const openSearch = () => {
    setSearch('');
    setTemplates(null);
    setSearchIsOpen(true);
  };

  const applyTemplate = (template: IOfferTextTemplate, append: boolean) => {
    onChange(append ? appendOfferText(value, template.text) : template.text);
    setSearchIsOpen(false);
  };

  const getPreview = (text: string) => {
    const plain = offerTextToPlainText(text).replace(/\s+/g, ' ');
    return plain.length > PREVIEW_LENGTH
      ? `${plain.slice(0, PREVIEW_LENGTH)}…`
      : plain;
  };

  return (
    <div className="offer-text-field">
      <RichOfferTextEditor
        disabled={locked}
        label={label}
        onChange={onChange}
        tall={tall}
        value={value}
        wide
      />
      <div className="offer-text-field-actions">
        <LinkButton onClick={openSave}>
          {t(`${translations.offers}:${textsKey}.Save offer text`)}
        </LinkButton>
        {locked ? null : (
          <LinkButton onClick={openSearch}>
            {t(`${translations.offers}:${textsKey}.Fetch offer text`)}
          </LinkButton>
        )}
      </div>

      <Modal isOpen={saveIsOpen} toggle={() => setSaveIsOpen(false)} centered>
        <ModalHeader
          className="modal-header"
          toggle={() => setSaveIsOpen(false)}
        >
          {t(`${translations.offers}:${textsKey}.Save offer text`)}
        </ModalHeader>
        <ModalBody>
          {textIsEmpty ? (
            <p>{t(`${translations.offers}:${textsKey}.Text is empty`)}</p>
          ) : (
            <>
              <Label className="label" for="offer-text-template-name">
                {t(`${translations.offers}:${textsKey}.Name`)}
              </Label>
              <Input
                autoFocus
                id="offer-text-template-name"
                maxLength={200}
                onChange={(event) => setName(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    saveTemplate();
                  }
                }}
                value={name}
              />
              <p className="offer-text-field-info">
                {t(`${translations.offers}:${textsKey}.Save info`)}
              </p>
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button
            className="modal-button"
            disabled={textIsEmpty || saving || name.trim() === ''}
            onClick={saveTemplate}
          >
            {t(`${translations.common}:Save`)}
          </Button>
          <Button className="modal-button" onClick={() => setSaveIsOpen(false)}>
            {t(`${translations.common}:Cancel`)}
          </Button>
        </ModalFooter>
      </Modal>

      <ModalConfirm
        showing={templateToDelete !== undefined}
        setShowing={() => setTemplateToDelete(undefined)}
        header={t(`${translations.offers}:${textsKey}.Delete offer text`)}
        text={t(`${translations.offers}:${textsKey}.Delete confirmation`, {
          name: templateToDelete?.name ?? '',
        })}
        yesButton={t(`${translations.common}:Confirm`)}
        noButton={t(`${translations.common}:Cancel`)}
        confirm={deleteTemplate}
      />

      <Modal
        isOpen={searchIsOpen}
        toggle={() => setSearchIsOpen(false)}
        centered
        size="xl"
      >
        <ModalHeader
          className="modal-header"
          toggle={() => setSearchIsOpen(false)}
        >
          {t(`${translations.offers}:${textsKey}.Fetch offer text`)}
        </ModalHeader>
        <ModalBody>
          <Input
            autoFocus
            onChange={(event) => setSearch(event.target.value)}
            placeholder={t(`${translations.offers}:${textsKey}.Search`)}
            value={search}
          />
          {templates === null ? (
            <p className="offer-text-field-info">
              {t(`${translations.offers}:${textsKey}.Searching`)}
            </p>
          ) : null}
          {templates !== null && templates.length === 0 ? (
            <p className="offer-text-field-info">
              {t(`${translations.offers}:${textsKey}.No texts`)}
            </p>
          ) : null}
          {templates !== null && templates.length > 0 ? (
            <Table size="sm" hover className="offer-text-templates">
              <thead>
                <tr>
                  <th>{t(`${translations.offers}:${textsKey}.Name`)}</th>
                  <th>{t(`${translations.offers}:${textsKey}.Text`)}</th>
                  <th>{t(`${translations.offers}:${textsKey}.Saved by`)}</th>
                  <th aria-label="actions" />
                </tr>
              </thead>
              <tbody>
                {templates.map((template) => (
                  <tr key={template.id}>
                    <td className="offer-text-template-name">
                      {template.name}
                    </td>
                    <td className="offer-text-template-preview">
                      {getPreview(template.text)}
                    </td>
                    <td className="offer-text-template-saved-by">
                      {template.createdUserName}
                      <br />
                      {template.organizationUnitName}
                      <br />
                      {formatDate(template.created)}
                    </td>
                    <td className="offer-text-template-actions">
                      <Button
                        className="modal-button"
                        onClick={() => applyTemplate(template, false)}
                        size="sm"
                      >
                        {t(`${translations.offers}:${textsKey}.Replace`)}
                      </Button>
                      <Button
                        className="modal-button"
                        disabled={textIsEmpty}
                        onClick={() => applyTemplate(template, true)}
                        size="sm"
                      >
                        {t(`${translations.offers}:${textsKey}.Append`)}
                      </Button>
                      {canDelete(template) ? (
                        <Button
                          className="modal-button offer-text-template-delete"
                          onClick={() => setTemplateToDelete(template)}
                          size="sm"
                        >
                          {t(`${translations.common}:Delete`)}
                        </Button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : null}
        </ModalBody>
        <ModalFooter>
          <Button
            className="modal-button"
            onClick={() => setSearchIsOpen(false)}
          >
            {t(`${translations.offers}:Close`)}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default OfferTextField;
